import { useState } from "react";
import { Box, Stack, useTheme } from "@mui/material"
import { enqueueSnackbar } from "notistack";
import { createAttachment, updateStatusCloseDetails } from "../../../../../apis";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import EGFileUpload from "../../../../MainComponents/FileUpload/EGFileUpload/EGFileUpload";
import { CommentFiles } from "./CommentFiles";
import { SubmitButtons } from "./CloseReport";
import { RelatedReportsClosureSection } from "./RelatedReportsClosureSection";
import { useTranslation } from "react-i18next";

export const ClosedUndetermined = ({
  id,
  reportDetail,
  closeDate,
  reportExpired,
  reportRender,
  editClosed,
  setEditClosed,
}) => {

  const [t] = useTranslation("report");
  const theme = useTheme();

  const [undeterminedReason, setUndeterminedReason] = useState(reportDetail?.status?.details?.undetermined_reason || "");
  const [observations, setObservations] = useState(reportDetail?.status?.details?.observations || "");
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState(reportDetail?.status?.details?.files || []);
  const [isLoading, setIsLoading] = useState(false);

  // Estados para cerrar denuncias relacionadas
  const [closeLinkedReports, setCloseLinkedReports] = useState(false);
  const [closeAllReports, setCloseAllReports] = useState(true);
  const [selectedReports, setSelectedReports] = useState([]);

  // Opciones para el motivo de desestimación (catálogos)
  const undeterminedReasonOptions = [
    { value: "loss_of_object", label: t("Conclusion.undetermined_loss_of_object") },
    { value: "other_area", label: t("Conclusion.undetermined_other_area") },
    { value: "post_mortem", label: t("Conclusion.undetermined_post_mortem") },
  ];

  const returnIsValid = () => {
    return undeterminedReason !== "";
  }

  // Obtener los reportes a cerrar
  const getReportsToClose = () => {
    if (!closeLinkedReports) return [];
    if (closeAllReports) return reportDetail?.related?.map((r) => r.id_report) || [];
    return selectedReports;
  };

  const handleSubmit = async (e) => {

    setIsLoading(true);

    let files_id;
    let filesToSend = [];

    enqueueSnackbar(`${t("Conclusion.sending")}...`, { variant: "info" });

    try {
      if (files.length > 0) {
        for (const fileObj of files) {
          const { formData } = fileObj;
          const filesRes = await createAttachment(formData);
          files_id = filesRes.attachments_id;
          filesToSend.push({
            file: files_id,
            name: fileObj.file.name,
            ...(!!fileObj.comment && {comment: fileObj.comment})
          });
        }
      }

      // Add current files
      if (uploadedFiles && uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          filesToSend.push({
            file: file.file,
            name: file.name,
            ...(!!file.comment && {comment: file.comment})
          });
        });
      }

      const details = {
        another_causes: [],
        followup: false,
        closed: "undefined",
        close_date: closeDate,
        causes: [],
        undetermined_reason: undeterminedReason,
        observations,
        files: filesToSend,
        expired_reports: reportExpired,
      };

      await updateStatusCloseDetails(id, details);

      // Cerrar las denuncias relacionadas si aplica
      const reportsToClose = getReportsToClose();
      if (reportsToClose.length > 0) {
        const closeDetails = {
          closed: "undefined",
          close_date: closeDate,
          undetermined_reason: undeterminedReason,
          observations,
          files: filesToSend,
          followup: false,
          causes: [],
          another_causes: [],
        };

        // Cerrar cada reporte seleccionado
        const closePromises = reportsToClose.map((reportId) =>
          updateStatusCloseDetails(reportId, closeDetails)
        );

        await Promise.all(closePromises);

        enqueueSnackbar(
          `${t("Conclusion.sending_successful")} - ${reportsToClose.length} ${t("Conclusion.related_reports_closed") || "denuncias relacionadas cerradas"}`,
          { variant: "success" }
        );
      } else {
        enqueueSnackbar(`${t("Conclusion.sending_successful")}`, {
          variant: "success",
        });
      }

      reportRender();
      setIsLoading(false);
      setEditClosed(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      enqueueSnackbar(`${t("Conclusion.error_closing_reports") || "Error al cerrar las denuncias"}`, {
        variant: "error",
      });
    }
  };

  return (
    <>
      <Box marginBottom={2}>
        <EGBasicSelect
          label={t("Conclusion.undetermined_reason")}
          placeholder={t("Conclusion.undetermined_reason_placeholder")}
          value={undeterminedReason}
          onChange={(e) => setUndeterminedReason(e.target.value)}
          options={undeterminedReasonOptions}
          required
        />
      </Box>

      <EGInput
        label={t("Conclusion.observations_type_close")}
        placeholder={t("Conclusion.observations_type_close_placeholder")}
        multiline
        rows={4}
        value={observations}
        onChange={(e) => setObservations(e.target.value)}
        sx={{ marginBottom: 2 }}
      />

      <Box marginBottom={2}>
        <EGFileUpload
          title={t("Conclusion.support_fiels")}
          files={files}
          setFiles={setFiles}
        />
      </Box>

      {
        (files.length > 0 || uploadedFiles.length > 0) &&
        <Stack border={`1px solid ${theme.palette.teal.main}`} borderRadius={1} marginBottom={2}>
          {
            files.length !== 0 &&
            <CommentFiles
              files={files}
              setFiles={setFiles}
              pathName="file.name"
              pathSize="file.size"
            />
          }

          {
            uploadedFiles.length !== 0 &&
            <CommentFiles
              files={uploadedFiles}
              setFiles={setUploadedFiles}
            />
          }
        </Stack>
      }

      {/* Sección para cerrar denuncias relacionadas */}
      <RelatedReportsClosureSection
        relatedReports={reportDetail?.related || []}
        closeLinkedReports={closeLinkedReports}
        setCloseLinkedReports={setCloseLinkedReports}
        closeAllReports={closeAllReports}
        setCloseAllReports={setCloseAllReports}
        selectedReports={selectedReports}
        setSelectedReports={setSelectedReports}
      />

      <SubmitButtons
        isLoading={isLoading}
        disabledSave={!returnIsValid}
        onSave={handleSubmit}
        onCancel={() => setEditClosed(false)}
        hideCancel={!editClosed}
      />
      
    </>
  )
}
